import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  Button,
  Alert,
} from '@mui/material';
import {
  SchoolRounded,
  PersonRounded,
  DescriptionRounded,
  OpenInNewRounded,
  ArticleRounded,
  CheckCircleRounded,
  CalendarMonthRounded,
  EmailRounded,
  PhoneRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAdmissionDetail } from './hooks/use-admission-detail';
import { formatDate } from '../../lib/i18n-format';
import { callable } from '../../lib/callable';
import { errorText } from '../../lib/error-text';
import { useTenantStore } from '../../stores/tenant.store';

// ─── Status helpers ───────────────────────────────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  draft: 'Borrador',
  awaiting_applicant: 'Esperando al Postulante',
  form_completed: 'Formulario Completado',
  submitted: 'Enviada a Revisión',
  under_review: 'En Revisión',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
  waitlisted: 'Lista de Espera',
  withdrawn: 'Retirada',
};

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'warning' | 'info'> = {
  draft: 'default',
  awaiting_applicant: 'info',
  form_completed: 'info',
  submitted: 'warning',
  under_review: 'warning',
  accepted: 'success',
  rejected: 'error',
  waitlisted: 'default',
  withdrawn: 'default',
};

// ─── Section Heading ──────────────────────────────────────────────────────────

function SectionHeading({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <Stack direction="row" alignItems="center" gap={1} mb={2}>
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography
        variant="overline"
        sx={{
          fontSize: '0.7rem',
          fontWeight: 800,
          letterSpacing: 1.5,
          color: 'text.secondary',
        }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

// ─── Info Row ─────────────────────────────────────────────────────────────────

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string | undefined;
}) {
  if (!value) return null;
  return (
    <Stack direction="row" alignItems="flex-start" gap={1.5}>
      <Box sx={{ color: 'text.disabled', mt: 0.25, display: 'flex', flexShrink: 0 }}>{icon}</Box>
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: 'text.disabled',
            display: 'block',
            mb: 0.25,
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
          }}
        >
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function AdmissionApplicationPage() {
  const { id } = useParams<{ id: string }>();
  const { detail, loading, error } = useAdmissionDetail(id);
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const [openError, setOpenError] = useState<string | null>(null);

  // S0·SEC-6 (4): el archivo se abre con una URL firmada por el servidor, que
  // comprueba el cargo y que la ruta sea de esta admisión. La ventana se abre
  // ANTES de esperar al servidor: si se abre después, el navegador la bloquea.
  const handleOpenDocument = async (storagePath: string) => {
    setOpenError(null);
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    try {
      const fn = callable<
        { tenantId: string; admissionId: string; storagePath: string },
        { url: string }
      >('admissionApplicantDocumentUrl');
      const res = await fn({ tenantId: currentTenantId ?? '', admissionId: id ?? '', storagePath });
      if (win) win.location.href = res.data.url;
      else window.open(res.data.url, '_blank');
    } catch (e) {
      win?.close();
      setOpenError(errorText(e, t('admissions.applicationDocs.openError')));
    }
  };

  // ── Loading / Error ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0d1117',
        }}
      >
        <CircularProgress sx={{ color: '#6366f1' }} />
      </Box>
    );
  }

  if (error || !detail) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0d1117',
        }}
      >
        <Typography color="error" variant="body1">
          {error ?? 'Postulación no encontrada'}
        </Typography>
      </Box>
    );
  }

  const hasFormData = detail.applicantFormData && Object.keys(detail.applicantFormData).length > 0;
  const formEntries = hasFormData ? Object.entries(detail.applicantFormData!) : [];
  const getLabel = (fieldId: string) => detail.formTemplateFieldLabels?.[fieldId] ?? fieldId;

  // ── No form submitted yet ─────────────────────────────────────────────────

  if (!hasFormData) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#0d1117',
        }}
      >
        <Paper
          variant="outlined"
          sx={{
            p: 5,
            borderRadius: 3,
            textAlign: 'center',
            maxWidth: 420,
            border: '1px solid rgba(255,255,255,0.08)',
            bgcolor: 'rgba(255,255,255,0.03)',
          }}
        >
          <ArticleRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} mb={1}>
            Formulario aún no completado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            El postulante no ha enviado su formulario todavía.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#0d1117',
        py: { xs: 4, md: 6 },
        px: { xs: 2, md: 3 },
      }}
    >
      {/* ── Document Container ── */}
      <Box sx={{ maxWidth: 780, mx: 'auto' }}>
        {/* ── Document Header Card ── */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(99,102,241,0.2)',
            background:
              'linear-gradient(135deg, rgba(99,102,241,0.08) 0%, rgba(139,92,246,0.05) 100%)',
          }}
        >
          {/* Top accent bar */}
          <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)' }} />

          <Box sx={{ p: { xs: 3, md: 4 } }}>
            {/* Institution + Doc type */}
            <Stack
              direction="row"
              alignItems="flex-start"
              justifyContent="space-between"
              gap={2}
              mb={3}
              flexWrap="wrap"
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.disabled',
                    textTransform: 'uppercase',
                    letterSpacing: 1.2,
                    fontWeight: 700,
                    fontSize: '0.65rem',
                  }}
                >
                  Dosfilos Seminary
                </Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: 0.25, lineHeight: 1.2 }}>
                  Postulación al Programa
                </Typography>
              </Box>
              <Chip
                label={STATUS_LABEL[detail.status] ?? detail.status}
                color={STATUS_COLOR[detail.status] ?? 'default'}
                size="small"
                sx={{ fontWeight: 700, mt: 0.5 }}
              />
            </Stack>

            {/* Program info grid */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2.5,
              }}
            >
              <InfoRow
                icon={<SchoolRounded sx={{ fontSize: 16 }} />}
                label="Programa"
                value={detail.programName}
              />
              <InfoRow
                icon={<CalendarMonthRounded sx={{ fontSize: 16 }} />}
                label="Período / Ciclo"
                value={detail.termName}
              />
              {detail.applicantCompletedAt && (
                <InfoRow
                  icon={<CheckCircleRounded sx={{ fontSize: 16 }} />}
                  label="Fecha de Envío"
                  value={formatDate(detail.applicantCompletedAt, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* ── Applicant Info Card ── */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.07)',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}
        >
          <SectionHeading
            icon={<PersonRounded sx={{ fontSize: 16 }} />}
            label="Datos del Postulante"
          />
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2.5,
            }}
          >
            <InfoRow
              icon={<PersonRounded sx={{ fontSize: 15 }} />}
              label="Nombre Completo"
              value={detail.personName}
            />
            <InfoRow
              icon={<EmailRounded sx={{ fontSize: 15 }} />}
              label="Correo Electrónico"
              value={detail.personEmail}
            />
            <InfoRow
              icon={<PhoneRounded sx={{ fontSize: 15 }} />}
              label="Teléfono"
              value={detail.personPhone}
            />
          </Box>
        </Paper>

        {/* ── Form Responses Card ── */}
        <Paper
          variant="outlined"
          sx={{
            mb: 3,
            p: { xs: 3, md: 4 },
            borderRadius: 3,
            border: '1px solid rgba(255,255,255,0.07)',
            bgcolor: 'rgba(255,255,255,0.02)',
          }}
        >
          <SectionHeading
            icon={<ArticleRounded sx={{ fontSize: 16 }} />}
            label="Respuestas del Formulario"
          />

          <Stack gap={3.5} divider={<Divider sx={{ borderColor: 'rgba(255,255,255,0.06)' }} />}>
            {formEntries.map(([fieldId, value]) => {
              const label = getLabel(fieldId);
              const isBool = typeof value === 'boolean';
              const isLong = typeof value === 'string' && value.length > 100;

              return (
                <Box key={fieldId}>
                  <Typography
                    variant="overline"
                    sx={{
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      letterSpacing: 1.2,
                      color: 'primary.main',
                      display: 'block',
                      mb: 1,
                      opacity: 0.85,
                    }}
                  >
                    {label}
                  </Typography>

                  {isBool ? (
                    <Chip
                      label={value ? 'Sí' : 'No'}
                      size="small"
                      color={value ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ fontWeight: 700 }}
                    />
                  ) : isLong ? (
                    <Typography
                      variant="body1"
                      sx={{
                        lineHeight: 1.85,
                        whiteSpace: 'pre-wrap',
                        color: 'text.primary',
                        fontWeight: 400,
                        borderLeft: '3px solid rgba(99,102,241,0.4)',
                        pl: 2,
                      }}
                    >
                      {value || '—'}
                    </Typography>
                  ) : (
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>
                      {String(value) || '—'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </Paper>

        {/* ── Documents Card ── */}
        {detail.applicantDocuments && detail.applicantDocuments.length > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mb: 3,
              p: { xs: 3, md: 4 },
              borderRadius: 3,
              border: '1px solid rgba(255,255,255,0.07)',
              bgcolor: 'rgba(255,255,255,0.02)',
            }}
          >
            <SectionHeading
              icon={<DescriptionRounded sx={{ fontSize: 16 }} />}
              label={t('admissions.applicationDocs.title')}
            />
            {openError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setOpenError(null)}>
                {openError}
              </Alert>
            )}
            <Stack gap={1.5}>
              {detail.applicantDocuments.map((doc, i) => (
                <Stack
                  key={i}
                  direction="row"
                  alignItems="center"
                  gap={2}
                  onClick={() => handleOpenDocument(doc.storagePath)}
                  sx={{
                    cursor: 'pointer',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid rgba(99,102,241,0.2)',
                    bgcolor: 'rgba(99,102,241,0.04)',
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      bgcolor: 'rgba(99,102,241,0.1)',
                      borderColor: 'rgba(99,102,241,0.4)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(99,102,241,0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <DescriptionRounded sx={{ fontSize: 20, color: 'primary.main' }} />
                  </Box>
                  <Box flex={1} minWidth={0}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, color: 'primary.main', mb: 0.25 }}
                    >
                      {doc.fileName ||
                        getLabel(doc.fieldId) ||
                        t('admissions.applicationDocs.fallbackName')}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.disabled', fontSize: '0.7rem' }}
                    >
                      {doc.uploadedAt
                        ? t('admissions.applicationDocs.uploadedOn', {
                            date: formatDate(doc.uploadedAt, {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            }),
                          })
                        : t('admissions.applicationDocs.fallbackName')}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewRounded sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      flexShrink: 0,
                      fontSize: '0.72rem',
                      borderColor: 'rgba(99,102,241,0.4)',
                      color: 'primary.main',
                      '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(99,102,241,0.1)' },
                    }}
                  >
                    {t('admissions.applicationDocs.open')}
                  </Button>
                </Stack>
              ))}
            </Stack>
          </Paper>
        )}

        {/* ── Footer ── */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
            Dosfilos Seminary · Sistema de Información ·{' '}
            {formatDate(new Date(), {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
